/**
 * The perf governor for background effects. Pure reducer — no DOM, no React.
 *
 * "Degrade rather than tank the framerate" is a measurable claim, so it is
 * measured: every composited frame reports how long it took, and an EMA of that
 * cost walks the pipeline down a four-rung ladder. Tier 3 turns the effect OFF
 * and says so, which is always better than shipping a 6 fps camera to the SFU —
 * a stuttering publisher wastes exactly as much egress as a smooth one.
 */
export type EffectTier = 0 | 1 | 2 | 3;

export interface TierSpec {
  /** Run segmentation on every Nth composited frame; reuse the mask between. */
  readonly segmentEvery: number;
  /** Composite resolution as a fraction of the captured frame. */
  readonly scale: number;
  /** Shown to the user when this tier is reached by degradation. */
  readonly reason: string | null;
}

export const TIER_SPECS: Readonly<Record<EffectTier, TierSpec>> = {
  0: { segmentEvery: 1, scale: 1, reason: null },
  1: {
    segmentEvery: 2,
    scale: 1,
    reason: 'Background effect reduced — this device is busy.',
  },
  2: {
    segmentEvery: 3,
    scale: 0.6,
    reason: 'Background effect running at lower detail to protect your frame rate.',
  },
  3: {
    segmentEvery: 0,
    scale: 1,
    reason: 'Background effect turned off — this device cannot run it smoothly.',
  },
} as const;

/** Composite must fit well inside a 24 fps frame, leaving room for the encoder. */
export const FRAME_BUDGET_MS = 22;
const ALPHA = 0.15;
const DEGRADE_STREAK = 24;
const RECOVER_STREAK = 150;
const RECOVER_RATIO = 0.55;

export interface BudgetState {
  readonly tier: EffectTier;
  readonly emaMs: number;
  readonly over: number;
  readonly under: number;
}

export const initialBudget: BudgetState = { tier: 0, emaMs: 0, over: 0, under: 0 };

const nextTier = (tier: EffectTier, delta: 1 | -1): EffectTier => {
  const value = tier + delta;
  if (value <= 0) return 0;
  if (value >= 3) return 3;
  return value === 1 ? 1 : 2;
};

/** Feed one composited frame's duration. Returns the (possibly new) state. */
export function stepBudget(state: BudgetState, frameMs: number): BudgetState {
  if (state.tier === 3) return state;

  const emaMs = state.emaMs === 0 ? frameMs : state.emaMs * (1 - ALPHA) + frameMs * ALPHA;
  const over = emaMs > FRAME_BUDGET_MS ? state.over + 1 : 0;
  const under = emaMs < FRAME_BUDGET_MS * RECOVER_RATIO ? state.under + 1 : 0;

  if (over >= DEGRADE_STREAK) {
    return { tier: nextTier(state.tier, 1), emaMs, over: 0, under: 0 };
  }
  if (state.tier > 0 && under >= RECOVER_STREAK) {
    return { tier: nextTier(state.tier, -1), emaMs, over: 0, under: 0 };
  }
  return { tier: state.tier, emaMs, over, under };
}
